import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";

import BackEndService from "../../api/BackEndService";
import CustomerService from "../../api/CustomerService";
import PropertyService from "../../api/PropertyService";
import { useInfiniteScroll } from "../../hooks/useInfiniteScroll";
import { generateUUIDFromID } from "../../services/UUIDGeneratorService";
import { RootState } from "../../store";
import { searchActions } from "../../store/search-slice";
import { activeTabsActions } from "../../store/tabs-slice";
import { userManagementActions } from "../../store/user-management-slice";
import { SevDeskCustomer } from "../../types/customer";
import { Technician, UsersQueryParams } from "../../types/user";
import { sortCustomers } from "../../utils/sortCustomers";
import { List } from "../List/List";
import { LoadingSpinner } from "../LoadingSpinner/LoadingSpinner";
import Search from "../Search/Search";
import UserListComponent from "../UserListComponent/UserListComponent";

import styles from "./kunden.module.css";

interface KundenProps {}

export const Kunden: React.FC<KundenProps> = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const searchValue = useSelector((state: RootState) => state.search.value);
  const initialCustomers = useSelector(
    (state: RootState) => state.userManagement.initialCustomers
  );

  const customers = useSelector(
    (state: RootState) => state.userManagement.customers
  );
  const customerUsers = useSelector(
    (state: RootState) => state.userManagement.customerUsers
  );

  const kundenStep = useSelector(
    (state: RootState) => state.tabs.userManagement.kundenStep
  );

  const selectedCustomer = useSelector(
    (state: RootState) => state.userManagement.selectedCustomer
  );

  const scrollableWrapper = document.getElementById(
    "scrollable-wrapper"
  ) as HTMLElement;
  const [isFetching, setIsFetching] = useInfiniteScroll(
    scrollableWrapper,
    hasMore
  );

  const fetchCustomers = async () => {
    setLoading(true);
    try {
      const customers = await CustomerService.GetSevDeskCustomers();

      const sortedCustomers = sortCustomers(customers);

      dispatch(userManagementActions.setCustomers(sortedCustomers));
      dispatch(userManagementActions.setInitialCustomers(sortedCustomers));
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (searchValue.length >= 3) {
      const lowerCaseSearchValue = searchValue.toLowerCase();
      const filtered = initialCustomers.filter(
        (customer: SevDeskCustomer) =>
          customer.name?.toLowerCase().includes(lowerCaseSearchValue) ||
          customer.surename?.toLowerCase().includes(lowerCaseSearchValue) ||
          customer.familyname?.toLowerCase().includes(lowerCaseSearchValue) ||
          (customer.surename + " " + customer.familyname)
            .toLowerCase()
            .includes(lowerCaseSearchValue)
      );
      dispatch(userManagementActions.setCustomers(filtered));
    } else if (searchValue.length === 0) {
      dispatch(userManagementActions.setCustomers(initialCustomers));
    }
  }, [searchValue, initialCustomers]);

  const fetchCustomerUsersById = async (
    customerId: string,
    pageNumber: number
  ) => {
    if (!location?.state?.customerDeleted) setLoading(true);
    const _customerId = generateUUIDFromID(customerId?.toString()!);
    let result: Technician[] = [];
    const params: UsersQueryParams = {
      "filter[customerId]": _customerId,
      sort: "lastName",
      "page[number]": pageNumber,
      "page[size]": 8,
    };

    try {
      const response = await CustomerService.GetCustomerUsers(params);
      result = response.data.map((item: Technician) => ({
        ...item,
        email: item.loginName,
      }));

      const newUsers =
        pageNumber === 1 ? result : [...customerUsers, ...result];

      setHasMore(result.length === 8);

      dispatch(userManagementActions.setCustomerUsers(newUsers));
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if ((hasMore || page === 1) && selectedCustomer) {
      fetchCustomerUsersById(selectedCustomer.id, page);
    }
  }, [page, selectedCustomer]);

  useEffect(() => {
    if (isFetching) {
      setPage((prevPage) => prevPage + 1);
      setIsFetching(false);
    }
  }, [isFetching]);

  const onSelectCustomer = async (customer: Customer) => {
    try {
      dispatch(activeTabsActions.setKundenStep(2));
      dispatch(userManagementActions.setSelectedCustomer(customer));

      dispatch(searchActions.setIsExpanded(false));
      dispatch(searchActions.setSearchValue(""));

      setPage(1);
      setHasMore(true);

      await getProperties(customer.id);
    } catch (error) {
      console.error(error);
    }
  };

  const getProperties = async (customerId: string) => {
    try {
      setLoading(true);
      const _customerId = generateUUIDFromID(customerId?.toString()!);
      const sevdeskProperties = await BackEndService.GetObjects(customerId);
      const properties = await PropertyService.GetProperties({
        "filter[customerId]": _customerId,
      });

      dispatch(
        userManagementActions.setCustomerProperties([
          ...sevdeskProperties.data.map((property) => ({
            ...property,
            id: generateUUIDFromID(property.id),
          })),
          ...properties.data,
        ])
      );
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (customers.length === 0) {
      fetchCustomers();
    }

    if (selectedCustomer && kundenStep === 2 && customerUsers.length === 0) {
      onSelectCustomer(selectedCustomer);
    }
  }, []);

  useEffect(() => {
    if (location?.state?.customerDeleted && selectedCustomer) {
      fetchCustomerUsersById(selectedCustomer.id, 1);
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [location?.state?.customerDeleted]);

  return (
    <div className={styles["container"]}>
      {loading && page === 1 ? (
        <LoadingSpinner loading={loading} />
      ) : kundenStep === 2 ? (
        <div>
          <p className={styles["container__customer-name"]}>
            {selectedCustomer?.name}
          </p>

          <UserListComponent
            users={customerUsers}
            loading={loading}
            page={page}
            customerAdmin={true}
          />
        </div>
      ) : (
        <div>
          <div className={styles["top-bar"]}>
            <Search />
          </div>
          <List
            hideShadow={true}
            data={customers.map((customer: SevDeskCustomer) => {
              const address = customer.addresses?.find(
                (address) => address.category?.id === "47"
              );

              return {
                id: customer.id.toString(),
                name:
                  customer.name ||
                  `${customer.surename} ${customer.familyname}`,
                address: address
                  ? `${address.street ?? ""}, ${address.zip ?? ""} ${address.city ?? ""}`
                  : "",
              };
            })}
            onItemSelected={(customer: any) => {
              onSelectCustomer(customer);
            }}
          />
        </div>
      )}
    </div>
  );
};
